"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { trackEvent } from "@/lib/analytics";
import Link from "next/link";
import { Loader2, Mail, Lock, User, ArrowRight } from "lucide-react";
import { CustomerAuthError, customerLogin, customerRegister } from "@/lib/customer-auth";

export type LoginFormCopy = {
    loginTitle: string;
    loginSub: string;
    email: string;
    password: string;
    login: string;
    processing: string;
    noAccount: string;
    toRegister: string;
    invalidCredentials: string;
    accountDisabled: string;
    validationError: string;
    loginFailed: string;
};

export type RegisterFormCopy = {
    registerTitle: string;
    registerSub: string;
    name: string;
    email: string;
    password: string;
    register: string;
    processing: string;
    haveAccount: string;
    toLogin: string;
    emailExists: string;
    validationError: string;
    registerFailed: string;
};

function Field({ icon: Icon, ...props }: { icon: React.ComponentType<{ className?: string }> } & React.InputHTMLAttributes<HTMLInputElement>) {
    // Placeholder alone is not a name: it vanishes on input and screen readers
    // may skip it. A visually hidden label keeps the visual design unchanged.
    return (
        <label className="relative block">
            <span className="sr-only">{props.placeholder}</span>
            <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
            <input
                {...props}
                className="w-full h-12 pl-11 pr-4 rounded-xl border border-slate-200 bg-white text-base focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition"
            />
        </label>
    );
}

export function LoginForm({ locale, copy }: { locale: string; copy: LoginFormCopy }) {
    // The recovery line below is the ONE string on this screen that does not
    // come through `copy`. Adding a field to `copy` would mean adding a key to
    // the `auth` namespace in messages/*.json, and those files are merged by a
    // single step at the end of this round; `accountSecurity` is this package's
    // own namespace and carries the same sentence on the Hesap tab.
    const tSecurity = useTranslations("accountSecurity");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await customerLogin(email.trim(), password);
            trackEvent("login", { method: "email" });
            // The account owns payment SDKs. A fresh document acquires its
            // payment-compatible CSP instead of retaining the public policy.
            // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- Changing document CSP requires full navigation.
            window.location.assign(`/${locale}/hesabim`);
        } catch (e: unknown) {
            if (e instanceof CustomerAuthError) {
                if (e.code === "INVALID_CREDENTIALS") setErr(copy.invalidCredentials);
                else if (e.code === "ACCOUNT_DISABLED") setErr(copy.accountDisabled);
                else if (e.code === "VALIDATION_ERROR") setErr(copy.validationError);
                else setErr(copy.loginFailed);
            } else {
                setErr(copy.loginFailed);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthShell title={copy.loginTitle} sub={copy.loginSub}>
            <form onSubmit={submit} className="space-y-3">
                <Field icon={Mail} type="email" required placeholder={copy.email} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                <Field icon={Lock} type="password" required placeholder={copy.password} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
                {err && <p role="alert" className="text-sm text-red-600">{err}</p>}
                <button type="submit" disabled={loading} aria-busy={loading} className="w-full h-12 rounded-xl bg-gradient-to-r from-emerald-700 to-teal-700 text-white font-bold flex items-center justify-center gap-2 hover:opacity-95 active:scale-[0.99] transition disabled:opacity-60">
                    {loading ? <><Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> {copy.processing}</> : <>{copy.login} <ArrowRight className="w-4 h-4" aria-hidden="true" /></>}
                </button>
            </form>
            {/*
                A REAL recovery link, and it is worth saying why it is here.

                This screen deliberately carried no "forgot my password" link
                for as long as nothing was behind it: a dead link on the one
                screen somebody reaches when they are already locked out is
                worse than an honest absence. That changed. The flow exists end
                to end -- POST /api/auth/password/forgot and
                POST /api/auth/password/reset (router.go:502-503), a token
                table, and the screen at app/[locale]/sifre-sifirla/page.tsx.

                It is also honest when mail cannot be sent: /forgot answers 503
                where no SMTP transport is configured, and the target screen
                turns that into "use support" rather than "check your inbox"
                (lib/account/password-reset-api.ts:36). So the link never
                promises an e-mail this system cannot send.
            */}
            {/*
                Guarded by `t.has`, and the guard is not defensive noise.

                `accountSecurity` reaches the browser through
                CLIENT_MESSAGE_NAMESPACES in app/[locale]/layout.tsx, and that
                list can only name a namespace that already exists in
                messages/*.json -- `pickClientMessages` throws the locale build
                otherwise. This package's copy is still in messages/_staging/
                until the merge step, so for that window the namespace is not in
                the provider. next-intl 4 does not throw on a missing message;
                it renders the key path. "accountSecurity.login.forgotLink" as
                the recovery link on the SIGN-IN page is worse than no link, so
                this degrades to today's behaviour instead.
            */}
            {tSecurity.has("login.forgotLink") && (
                <p className="text-sm text-center mt-5">
                    <Link href={`/${locale}/sifre-sifirla`} className="inline-flex min-h-[44px] items-center text-emerald-800 font-semibold hover:underline">
                        {tSecurity("login.forgotLink")}
                    </Link>
                </p>
            )}
            <p className="text-sm text-slate-500 text-center mt-2">
                {copy.noAccount} <Link href={`/${locale}/kayit`} className="text-emerald-800 font-semibold hover:underline">{copy.toRegister}</Link>
            </p>
        </AuthShell>
    );
}

export function RegisterForm({ locale, copy }: { locale: string; copy: RegisterFormCopy }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await customerRegister(name.trim(), email.trim(), password);
            trackEvent("sign_up", { method: "email" });
            // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- Changing document CSP requires full navigation.
            window.location.assign(`/${locale}/hesabim`);
        } catch (e: unknown) {
            if (e instanceof CustomerAuthError) {
                if (e.code === "EMAIL_EXISTS") setErr(copy.emailExists);
                else if (e.code === "VALIDATION_ERROR") setErr(copy.validationError);
                else setErr(copy.registerFailed);
            } else {
                setErr(copy.registerFailed);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthShell title={copy.registerTitle} sub={copy.registerSub}>
            <form onSubmit={submit} className="space-y-3">
                <Field icon={User} type="text" required placeholder={copy.name} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
                <Field icon={Mail} type="email" required placeholder={copy.email} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                <Field icon={Lock} type="password" required minLength={6} placeholder={copy.password} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
                {err && <p role="alert" className="text-sm text-red-600">{err}</p>}
                <button type="submit" disabled={loading} aria-busy={loading} className="w-full h-12 rounded-xl bg-gradient-to-r from-emerald-700 to-teal-700 text-white font-bold flex items-center justify-center gap-2 hover:opacity-95 active:scale-[0.99] transition disabled:opacity-60">
                    {loading ? <><Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> {copy.processing}</> : <>{copy.register} <ArrowRight className="w-4 h-4" aria-hidden="true" /></>}
                </button>
            </form>
            <p className="text-sm text-slate-500 text-center mt-5">
                {copy.haveAccount} <Link href={`/${locale}/giris`} className="text-emerald-800 font-semibold hover:underline">{copy.toLogin}</Link>
            </p>
        </AuthShell>
    );
}

function AuthShell({ title, sub, children }: { title: string; sub: string; children: React.ReactNode }) {
    return (
        <div className="max-w-md mx-auto w-full">
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-7 sm:p-8">
                <h1 className="text-2xl font-extrabold text-slate-900">{title}</h1>
                <p className="text-sm text-slate-500 mt-1 mb-6">{sub}</p>
                {children}
            </div>
        </div>
    );
}
